import React, { useId } from 'react';
import type { Icon } from '@phosphor-icons/react';

/**
 * Formularfeld (README §4.4–4.6): Label 12/700 --ink3 über dem Feld,
 * Feld 50 px (kompakt 44), Radius 15, Fläche --bg mit Linie.
 */
export interface TextFeldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size'> {
  label: string;
  wert: string;
  onChange: (wert: string) => void;
  icon?: Icon;
  /** Text rechts im Feld, z. B. „€“ oder „m²“. */
  suffix?: string;
  /** Über die ganze Rasterbreite (grid-column 1/-1). */
  voll?: boolean;
  kompakt?: boolean;
  fehler?: string;
}

export function TextFeld({ label, wert, onChange, icon: IconKomp, suffix, voll, kompakt, fehler, id, ...rest }: TextFeldProps) {
  const eigeneId = useId();
  const feldId = id ?? eigeneId;
  const fehlerId = `${feldId}-fehler`;
  return (
    <div className="flex flex-col min-w-0" style={{ gap: 6, gridColumn: voll ? '1 / -1' : undefined }}>
      <label htmlFor={feldId} style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</label>
      <div
        className="flex items-center"
        style={{
          height: kompakt ? 44 : 50, borderRadius: kompakt ? 14 : 15, gap: 10, padding: '0 14px', background: 'var(--bg)',
          boxShadow: fehler ? 'inset 0 0 0 1.5px var(--bad)' : 'inset 0 0 0 1px var(--line)',
        }}
      >
        {IconKomp && <IconKomp size={17} color="var(--ink3)" aria-hidden />}
        <input
          id={feldId}
          value={wert}
          onChange={e => onChange(e.target.value)}
          aria-invalid={fehler ? true : undefined}
          aria-describedby={fehler ? fehlerId : undefined}
          className="flex-1 min-w-0 h-full"
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}
          {...rest}
        />
        {suffix && <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink3)' }}>{suffix}</span>}
      </div>
      {fehler && <span id={fehlerId} style={{ fontSize: 12, fontWeight: 600, color: 'var(--bad)', paddingLeft: 4 }}>{fehler}</span>}
    </div>
  );
}

/** Feldraster: repeat(auto-fit, minmax(180px, 1fr)), gap 12. */
export function FeldRaster({ children, minBreite = 180 }: { children: React.ReactNode; minBreite?: number }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${minBreite}px, 1fr))`, gap: 12 }}>{children}</div>;
}

export interface AuswahlOption<K extends string> {
  key: K;
  label: string;
  icon?: Icon;
}

/**
 * Auswahl als Chips (z. B. Lage EG/1.OG, Zimmer) oder Kacheln (Art: Wohnung/Garage).
 * Gewählt: Ring 2 px --acc auf --bg; sonst --fill2.
 */
export function Auswahl<K extends string>({ label, optionen, wert, onChange, art = 'chip' }: {
  label?: string;
  optionen: readonly AuswahlOption<K>[];
  wert: K | null;
  onChange: (k: K) => void;
  art?: 'chip' | 'kachel';
}) {
  const gruppenId = useId();
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {label && <span id={gruppenId} style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>}
      <div
        role="radiogroup"
        aria-labelledby={label ? gruppenId : undefined}
        style={art === 'kachel'
          ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }
          : { display: 'flex', flexWrap: 'wrap', gap: 8 }}
      >
        {optionen.map(o => {
          const an = o.key === wert;
          const IconKomp = o.icon;
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={an}
              onClick={() => onChange(o.key)}
              className="cursor-pointer"
              style={art === 'kachel'
                ? {
                  borderRadius: 16, padding: '12px 6px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, border: 'none',
                  background: an ? 'var(--bg)' : 'var(--fill2)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'none', color: 'var(--ink)', transition: 'all .2s',
                }
                : {
                  height: 40, padding: '0 14px', borderRadius: 13, display: 'inline-flex', alignItems: 'center', gap: 7, border: 'none',
                  fontSize: 13, fontWeight: an ? 800 : 700, background: an ? 'var(--bg)' : 'var(--fill2)',
                  boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'none', color: 'var(--ink)', transition: 'all .2s',
                }}
            >
              {IconKomp && <IconKomp weight="fill" size={art === 'kachel' ? 22 : 15} color="var(--acc)" />}
              <span style={art === 'kachel' ? { fontSize: 12, fontWeight: an ? 800 : 700 } : undefined}>{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
